import m from 'mithril'
import Marzipano from 'marzipano'

import { setAppState } from 'core/services/app-state'
import { tracks, setPosition, getSceneTracks } from 'core/services/audio-manager'
import { buildScene, buildHotspot, setHotspotClasses, positionalAudio } from 'panorama/helpers'

import Hotspot from 'panorama/components/Hotspot'
import Transition from 'panorama/components/Transition'
import Debugger from 'panorama/components/Debugger'
import ControlIndicator from 'panorama/components/ControlIndicator'

class Pano {
  constructor(vnode) {
    this.setVisibility = this._setVisibility.bind(this)
    this.onSceneChange = this._onSceneChange.bind(this)
    this.toggleScene = this._toggleScene.bind(this)
    this.toggleLogBook = vnode.attrs.toggleLogBook.bind(this)
    this.logScene = this._logScene.bind(this)
    this.loadScene = this._loadScene.bind(this)
    this.setSceneAudio = this._setSceneAudio.bind(this)
    this.changeScene = this._changeScene.bind(this)
    let state = this
  }

  oninit(vnode) {
    let state = this

    state.trackList = []
    state.positionalTracks = []
    state.sceneID = null
    state.scene = null
    state.viewer = null
    state.debugger = false
    state.scenes = {}
    state.lastViewed = []
    state.viewedOverlays = []
    state.hideTemplate = false
    state.sceneVisible = false
    state.autorotate = null
    state.controlIndicator = true
    state.viewedScenes = []
  }

  oncreate(vnode) {
    let state = this

    // Set Pano target & viewer.
    const target = vnode.dom.querySelector('.pano-target')
    state.viewer = new Marzipano.Viewer(target)

    this.changeScene(vnode)

    vnode.dom.addEventListener('pointerdown', () => {
      if (state.controlIndicator) {
        state.controlIndicator = false
        m.redraw()
      }
    })

    setTimeout(m.redraw, 1)
  }

  onupdate(vnode) {
    let state = this

    state.setVisibility(vnode, vnode.attrs.scene)

    state.changeScene(vnode)
  }

  _onSceneChange(vnode, scene) {
    let state = this
    state.lastViewed.push(scene.id)

    state.trackList = state.setSceneAudio(scene.audio)
    state.logScene(vnode, scene, (firstView) => {
      const options = { firstView: !!firstView }
      if (typeof scene.onInit === 'function') {
        scene.onInit.call(state, state.viewer, scene, options)
      }

      state.setVisibility(vnode, scene, options)
    })
  }

  _setVisibility(vnode, scene, options) {
    let state = this

    if (state.sceneVisible) return
    if (vnode.attrs.overlay) return
    if (typeof vnode.attrs.logBookPage === 'number') return

    state.sceneVisible = true

    if (typeof scene.onVisible === 'function') {
      scene.onVisible.call(state, state.viewer, scene, options)
    }

    m.redraw()
  }

  // Saves the scene ID to app state
  _logScene(vnode, scene, callback = () => {}) {
    let state = this

    const viewed = vnode.attrs.appState.viewedScenes || []
    state.viewedScenes = viewed
    if (viewed.indexOf(scene.id) !== -1) {
      return callback(false)
    }

    const viewedScenes = [...viewed, scene.id]
    
    setAppState({ viewedScenes }, () => {
      callback(true)
    })
  }
  _toggleScene() {
    let state = this
    const id = state.sceneID === 'outside' ? 'reactor' : 'outside'
    m.route.set(`/pano/${id}`)
  }

  _loadScene(scene) {
    let state = this

    if (!state.scenes[scene.id]) {
      state.scenes[scene.id] = buildScene.call(state, Marzipano, state.viewer, scene)

      // Loop through that scene's hotspots...
      const hotspots = scene.hotspots.map((hotspot) =>
        buildHotspot.call(state, hotspot, state.viewer, state.scenes[scene.id])
      )

      state.scenes[scene.id].addEventListener('viewChange', () => {
        setHotspotClasses(state.scenes[scene.id], hotspots)

        if (state.positionalTracks.length)
          setPosition(state.positionalTracks, state.scene.view().yaw())

        if (typeof scene.onChange === 'function')
          scene.onChange.call(state, state.viewer, state.scenes[scene.id], hotspots)
      })
    }

    return state.scenes[scene.id]
  }

  _setSceneAudio(audio) {
    let state = this
    if (!Array.isArray(audio)) return false

    const tr = tracks(audio, true)

    state.positionalTracks = positionalAudio(tr)

    setPosition(state.positionalTracks, state.scene.view().yaw())

    return tr
  }

  _changeScene(vnode) {
    return new Promise((resolve, reject) => {
      let state = this

      const scene = vnode.attrs.scene
      const sceneID = scene.id || null

      // Preload the next scene
      if (vnode.attrs.preload) {
        vnode.attrs.preload.map(state.loadScene)
      }

      // If the scene we're changing to has the same ID, do nothing.
      if (state.sceneID === sceneID) return resolve()

      // Update the scene ID to that we're changing to.
      state.sceneID = sceneID
      state.sceneVisible = false

      // Remove the autorotation
      state.viewer.stopMovement()
      state.viewer.setIdleMovement(Infinity)

      if (!sceneID) {
        state.trackList = state.setSceneAudio([])
        return resolve()
      }

      const nextScene = (state.scene = state.loadScene(scene))

      state.autorotate = Marzipano.autorotate({
        yawSpeed: 0.05,
        targetPitch: scene.orientation.pitch,
      })

      setTimeout(
        () => {
          // Actually switch the scene
          nextScene.switchTo(
            {
              transitionDuration: vnode.attrs.transition ? 0 : 300,
            },
            () => {
              this.onSceneChange(vnode, scene)
              resolve(scene)
            }
          )

          const orientation = {
            fov: vnode.attrs.f || scene.orientation.fov,
            yaw: vnode.attrs.y || scene.orientation.yaw,
            pitch: vnode.attrs.p || scene.orientation.pitch,
          }

          nextScene.lookTo(orientation, { transitionDuration: 0 })
        },
        vnode.attrs.transition ? 1000 : 0
      )
    })
  }

  view(vnode) {
    let state = this
    const transition = vnode.attrs.transition
    const Template = vnode.attrs.scene.template
    const hotspots = vnode.attrs.scenes.reduce((arr, scene) => {
      return [...arr, ...scene.hotspots]
    }, [])

    return (
      <div className="pano">
        <div className="pano-target" />

        {process.env.DEBUG && (
          <div className="pano-controls">
            <button
              className="pano-debugger-toggle"
              onclick={() => {
                state.debugger = !state.debugger
              }}>
              Toggle Debugger
            </button>
          </div>
        )}

        {hotspots
          .filter((hotspot) => !!hotspot.template)
          .map((hotspot) => (
            <Hotspot
              hotspot={hotspot}
              scene={state.scene}
              viewer={state.viewer}
              onclick={(e) => {
                if (typeof hotspot.click !== 'function') return
                hotspot.click.call(state, state.viewer, state.scene)
              }}
            />
          ))}

        {state.sceneVisible && state.controlIndicator && <ControlIndicator />}

        {Template && !state.hideTemplate && <Template scene={vnode.attrs.scene} />}

        {state.debugger && state.viewer && <Debugger viewer={state.viewer} />}

        {transition && <Transition transition={transition} />}
      </div>
    )
  }
}

export default Pano
